import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { doc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { alpha } from "@mui/material/styles";
import { CertificationChip, UNLOCK_CERTIFICATION_PRICE } from "./certifications";
import { logPayment } from "./payments";

const paypalOptions = {
  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture",
};

const UnlockCertificationDialog = ({ open, onClose, skill, score, onUnlocked }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCreateOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: `Mostrar certificación de ${skill} en el perfil`,
          amount: { value: String(UNLOCK_CERTIFICATION_PRICE), currency_code: "USD" },
        },
      ],
    });
  };

  const handleApprove = async (data, actions) => {
    try {
      setLoading(true);
      await actions.order.capture();

      const uid = auth.currentUser.uid;
      await updateDoc(doc(db, "users", uid), {
        [`skillAssessments.${skill}.displayed`]: true,
      });

      await logPayment({
        type: "certification",
        amount: UNLOCK_CERTIFICATION_PRICE,
        description: `Mostrar certificación de ${skill}`,
      });

      onUnlocked && onUnlocked();
      onClose();
    } catch (err) {
      console.error("Error unlocking certification:", err);
      setError("Ocurrió un error al procesar el pago. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mostrar certificación en tu perfil</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CertificationChip skill={skill} score={score} size="medium" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aprobaste el examen de <strong>{skill}</strong> con {score}%. Por USD$
          {UNLOCK_CERTIFICATION_PRICE} podés mostrar esta certificación en tu perfil público
          para que los clientes la vean.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <PayPalScriptProvider options={paypalOptions}>
          <Box sx={{ position: "relative", minHeight: 45 }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
                  zIndex: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <PayPalButtons
              style={{ shape: "rect", layout: "vertical", label: "pay" }}
              createOrder={handleCreateOrder}
              onApprove={handleApprove}
              onError={() => setError("Ocurrió un error con PayPal.")}
            />
          </Box>
        </PayPalScriptProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Ahora no
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnlockCertificationDialog;
